import React, { useState, useEffect } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";
import Modal from "../../components/shared/Modal";
import {
  BookmarkIcon,
  SparklesIcon,
  ShareIcon,
  PlusIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import api from '../../utils/apiConfig';

import logger from '../../utils/logger';

const FinancialNews = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAISummaryModal, setShowAISummaryModal] = useState(false);
  const [newsArticles, setNewsArticles] = useState([]);  // Initialize as empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const breadcrumbItems = [
    { label: "Education & Licensing", link: "/education-licensing" },
    { label: "Financial News", link: "/education-licensing/financial-news" },
  ];

  // Categories and topics remain the same
  const categories = [
    "All",
    "Market Updates",
    "Regulatory Changes",
    "Investment Strategies",
    "Tax News",
    "Economic Indicators",
  ];

  const topics = [
    "ESG Investing",
    "Insurance Trends",
    "Retirement Planning",
    "Tax Strategy",
    "Market Analysis",
    "Regulatory Compliance",
    "Practice Management",
    "Client Communication",
  ];

  // Fetch news data
  const fetchNews = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("primaryTab", activeTab === "all" ? "" : activeTab);
      if (selectedTopics.length > 0) {
        params.append("topics", selectedTopics.join(","));
      }
      params.append("limit", 20);
      
      console.log('🔍 [FinancialNews] Fetching news:', {
        activeTab,
        selectedTopics,
        queryParams: params.toString()
      });
      
      const { data } = await api.get(`/market/news`, { params });
      console.log('📦 [FinancialNews] Received news data:', {
        totalArticles: data.news?.length || 0,
        categories: data.news?.map(a => a.categories || []).flat(),
        firstArticle: data.news?.[0]
      });
      
      setNewsArticles(data.news || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('❌ [FinancialNews] Error fetching news:', err);
      setError(`Failed to load news: ${err.message}`);
      setNewsArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Add state debugging effect
  useEffect(() => {
    console.log('🔄 [FinancialNews] State update:', {
      activeTab,
      selectedTopics,
      totalArticles: newsArticles?.length || 0,
      loading,
      error,
      lastUpdated
    });
  }, [activeTab, selectedTopics, newsArticles, loading, error, lastUpdated]);

  // Filter articles based on active tab and selected topics
  const filteredArticles = (newsArticles || []).filter((article) => {
    if (!article) return false;
    
    // Check if article matches the active tab
    const matchesTab = activeTab === "all" || (
      (article.categories && article.categories.some(cat => 
        cat.toLowerCase() === activeTab.toLowerCase()
      )) ||
      (article.category && article.category.toLowerCase() === activeTab.toLowerCase())
    );

    // Check if article matches selected topics
    const matchesTopics =
      selectedTopics.length === 0 ||
      (article.topics && article.topics.some((topic) => selectedTopics.includes(topic)));

    // Debug log for filtered articles
    console.log('🔍 [FinancialNews] Article filtering:', {
      articleId: article.id,
      title: article.title,
      categories: article.categories,
      primaryCategory: article.category,
      matchesTab,
      matchesTopics,
      willShow: matchesTab && matchesTopics
    });

    return matchesTab && matchesTopics;
  });

  // Add effect to force refresh on mount
  useEffect(() => {
    console.log('🚀 [FinancialNews] Component mounted, forcing refresh');
    fetchNews(true);
  }, []); // Empty dependency array means this runs once on mount

  // Separate effect for filter changes
  useEffect(() => {
    console.log('📋 [FinancialNews] Filters changed:', { activeTab, selectedTopics });
    fetchNews();
  }, [activeTab, selectedTopics]);

  // Mock data for educational content (keep this for now)
  const educationalContent = [
    {
      id: "edu1",
      title: "Understanding the Impact of Geopolitical Events on Markets",
      type: "Webinar",
      provider: "Financial Planning Association",
      duration: "45 min",
      level: "Intermediate",
      topics: ["Market Analysis", "Risk Management"],
    },
    {
      id: "edu2",
      title: "Tax-Efficient Investment Strategies for High-Net-Worth Clients",
      type: "Course",
      provider: "Investment & Wealth Institute",
      duration: "2 hours",
      level: "Advanced",
      topics: ["Tax Strategy", "Investment Strategies"],
    },
  ];

  const handleTopicToggle = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic],
    );
  };

  const handleBookmark = (article) => {
    setSelectedArticle(article);
    setShowBookmarkModal(true);
  };

  const handleShare = (article) => {
    setSelectedArticle(article);
    setShowShareModal(true);
  };

  const handleAISummary = (article) => {
    setSelectedArticle(article);
    setShowAISummaryModal(true);
  };

  // Manual refresh handler
  const handleRefresh = () => {
    fetchNews(true);
  };

  return (
    <SubPageLayout
      title="Financial News"
      subtitle="Stay informed with the latest financial news and educational content"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Refresh button and error display */}
        <div className="flex justify-between items-center mb-4">
          <div>
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => fetchNews(true)}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-500"
              disabled={loading}
              title="Refresh News"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            {lastUpdated && (
              <span className="text-xs text-gray-500 dark:text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {/* Categories Navigation */}
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-4">
          <nav className="flex space-x-4">
            {categories && categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveTab(category.toLowerCase())}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === category.toLowerCase()
                    ? "bg-[#202EC6] text-white"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                {category}
              </button>
            ))}
          </nav>
        </div>

        {/* Topics Filter */}
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Topics of Interest
          </h3>
          <div className="flex flex-wrap gap-2">
            {topics && topics.map((topic) => (
              <button
                key={topic}
                onClick={() => handleTopicToggle(topic)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedTopics.includes(topic)
                    ? "bg-green-100 text-green-800 border-2 border-green-600"
                    : "bg-gray-100 text-gray-800 dark:text-gray-200 border-2 border-transparent hover:border-gray-300"
                }`}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* News Feed */}
          <div className="col-span-8">
            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202EC6] mx-auto"></div>
                  <p className="mt-2 text-gray-500 dark:text-gray-500">Loading news...</p>
                </div>
              ) : filteredArticles && filteredArticles.length > 0 ? (
                filteredArticles.map((article) => (
                  article && (
                    <div
                      key={article.id}
                      className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6 relative group"
                    >
                      <div className="absolute top-4 right-4 flex space-x-2">
                        <button
                          onClick={() => handleBookmark(article)}
                          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Bookmark"
                        >
                          <BookmarkIcon
                            className={`h-5 w-5 ${article.isBookmarked ? "text-[#202EC6]" : "text-gray-400"}`}
                          />
                        </button>
                        <button
                          onClick={() => handleShare(article)}
                          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Share"
                        >
                          <ShareIcon className="h-5 w-5 text-gray-400" />
                        </button>
                        <button
                          onClick={() => handleAISummary(article)}
                          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="AI Summary"
                        >
                          <SparklesIcon className="h-5 w-5 text-[#202EC6]" />
                        </button>
                      </div>
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:text-gray-200 rounded-md text-sm font-medium">
                          {article.category}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-500">
                          {article.type}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300 mb-4">{article.summary}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span>{article.source}</span>
                          <span>•</span>
                          <span>
                            {(() => {
                              try {
                                const dateObj = new Date(article.time);
                                return dateObj.toLocaleString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                              } catch {
                                return article.time || "No date available";
                              }
                            })()}
                          </span>
                          {article.readTime && (
                            <>
                              <span>•</span>
                              <span>{article.readTime} read</span>
                            </>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {article.topics && article.topics.map((topic) => (
                            <span
                              key={topic}
                              className="px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs"
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-500">
                  No news articles found for the selected filters.
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="col-span-4 space-y-6">
            {/* Educational Content */}
            <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Educational Resources
              </h2>
              <div className="space-y-4">
                {educationalContent && educationalContent.map((content) => (
                  <div
                    key={content.id}
                    className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">
                          {content.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {content.provider} • {content.duration}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:text-gray-200 rounded-md text-xs">
                            {content.type}
                          </span>
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:text-gray-200 rounded-md text-xs">
                            {content.level}
                          </span>
                        </div>
                      </div>
                      <button className="text-[#202EC6] hover:text-opacity-80">
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90 transition-colors">
                View All Resources
              </button>
            </div>

            {/* Trending Topics */}
            <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Trending Topics
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">#FedRateDecision</span>
                  <span className="text-sm text-gray-500 dark:text-gray-500">
                    1.2k discussions
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">#ESGInvesting</span>
                  <span className="text-sm text-gray-500 dark:text-gray-500">856 discussions</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">#TaxStrategy2024</span>
                  <span className="text-sm text-gray-500 dark:text-gray-500">623 discussions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bookmark Modal */}
      <Modal isOpen={showBookmarkModal && !!selectedArticle} onClose={() => setShowBookmarkModal(false)} maxWidth="md">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Save Article
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add to Collection
              </label>
              <select className="w-full p-2 border rounded-lg bg-surface-default dark:bg-surface-default text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700">
                <option>Market Updates</option>
                <option>Tax Planning</option>
                <option>Client Education</option>
                <option>+ Create New Collection</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add Note (Optional)
              </label>
              <textarea
                className="w-full p-2 border rounded-lg bg-surface-default dark:bg-surface-default text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                rows="3"
                placeholder="Add a note about this article..."
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowBookmarkModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowBookmarkModal(false)}
                className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Share Modal */}
      <Modal isOpen={showShareModal && !!selectedArticle} onClose={() => setShowShareModal(false)} maxWidth="md">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Share Article
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Share with Clients
              </label>
              <select className="w-full p-2 border rounded-lg bg-surface-default dark:bg-surface-default text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700">
                <option>Select Clients</option>
                <option>All Clients</option>
                <option>Custom Group</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add Message (Optional)
              </label>
              <textarea
                className="w-full p-2 border rounded-lg bg-surface-default dark:bg-surface-default text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                rows="3"
                placeholder="Add a personal message..."
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90"
              >
                Share
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* AI Summary Modal */}
      <Modal isOpen={showAISummaryModal && !!selectedArticle} onClose={() => setShowAISummaryModal(false)} maxWidth="2xl">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              AI Summary & Analysis
            </h2>
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Key Takeaways
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                {selectedArticle?.actionableInsights?.map((insight, index) => (
                  <li key={index}>{insight}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client Impact
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                This news may affect clients with significant fixed-income
                allocations or those planning major financial decisions in the
                near term.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Recommended Actions
              </h3>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded text-[#202EC6]" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Review affected client portfolios
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded text-[#202EC6]" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Prepare client communication
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded text-[#202EC6]" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Schedule strategy review
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default FinancialNews;
